// Admin Products Manager - Business logic for product purchase records
// Handles fetching and displaying product purchase data for admin

import { Manager } from '../../../../../core';
import { logger } from '../../../../../core';

const PRODUCT_COLORS = [
  '#8b0000', '#3b82f6', '#22c55e', '#f59e0b', '#ec4899',
  '#6366f1', '#14b8a6', '#f97316', '#94a3b8'
];

export class AdminProductsManager extends Manager {
  constructor() {
    super();
    this.allPurchases = [];
    this.chartData = [];
    this.total = 0;
    this.page = 1;
    this.limit = 25;
    this.summary = null;
    this.chartView = 'all'; // 'all' | 'byProduct'
    this.amountMode = 'actual'; // 'actual' | 'full'
    this.isLoading = false;
    this.filters = {};
  }

  async initGlobalVariables() {
    this.allPurchases = [];
    this.chartData = [];
    this.total = 0;
    this.page = 1;
    this.limit = 25;
    this.summary = null;
    this.chartView = 'all';
    this.amountMode = 'actual';
    this.isLoading = false;
    this.filters = {};
  }

  async initSystems() {
    await this.loadPurchases({ all: true });
    this.isInitialized = true;
    logger.log(
      { tags: 'admin-products-manager', color1: 'blue' },
      'AdminProductsManager initialized successfully'
    );
  }

  /**
   * Load product purchases from API (all at once for client-side pagination)
   * @param {Object} [opts] - { all, productId, userId, from, to }
   */
  async loadPurchases(opts = {}) {
    try {
      this.isLoading = true;
      const params = new URLSearchParams();
      if (opts.all) {
        params.set('all', 'true');
      } else {
        params.set('page', String(opts.page ?? this.page));
        params.set('limit', String(opts.limit ?? this.limit));
      }
      if (opts.productId) params.set('productId', opts.productId);
      if (opts.userId) params.set('userId', opts.userId);
      if (opts.from) params.set('from', opts.from);
      if (opts.to) params.set('to', opts.to);

      const response = await fetch(`/api/admin/product-purchases?${params.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product purchases');
      }

      const data = await response.json();
      if (data.success) {
        this.allPurchases = data.purchases || [];
        this.chartData = data.chartData || [];
        this.total = data.total ?? this.allPurchases.length;
        this.page = opts.all ? 1 : (data.page ?? 1);
        this.limit = opts.all ? 25 : (data.limit ?? 25);
        this.summary = data.summary || null;
        this.filters = { ...opts };
      } else {
        throw new Error(data.error || 'Invalid response');
      }
    } catch (err) {
      logger.error(
        { tags: 'admin-products-manager|error', color1: 'red' },
        'Failed to load product purchases:',
        err
      );
      this.allPurchases = [];
      this.total = 0;
    } finally {
      this.isLoading = false;
    }
  }

  /** Client-side pagination: update page without API call */
  setPage(page) {
    const totalPages = Math.max(1, Math.ceil(this.total / this.limit));
    this.page = Math.max(1, Math.min(totalPages, page));
  }

  /** Client-side pagination: update limit and reset to page 1 */
  setLimit(limit) {
    this.limit = limit;
    this.page = 1;
  }

  /** Returns the current page slice from allPurchases */
  getPurchases() {
    const start = (this.page - 1) * this.limit;
    return this.allPurchases.slice(start, start + this.limit);
  }

  getTotal() {
    return this.total;
  }

  getPage() {
    return this.page;
  }

  getLimit() {
    return this.limit;
  }

  /**
   * Get chart data for ProductTransactionChart (Stripe-compatible format).
   * @param {Object} [opts] - { chartView, amountMode }
   */
  getChartData(opts = {}) {
    const mode = opts.amountMode ?? this.amountMode;
    return this.chartData.map((row) => ({
      date: row.date,
      total: mode === 'full' ? (row.totalFull ?? row.total) : row.total,
      byPlan: mode === 'full' ? (row.byPlanFull ?? row.byPlan ?? {}) : (row.byPlan ?? {})
    }));
  }

  /**
   * Get summary stats in StripeSummaryStats-compatible format.
   * @param {Object} [opts] - { chartView, amountMode }
   */
  getSummaryStats(opts = {}) {
    const view = opts.chartView ?? this.chartView;
    const mode = opts.amountMode ?? this.amountMode;
    const s = this.summary;
    const totalRevenueCents = s?.totalRevenueCents ?? 0;
    const totalDiscountCents = s?.totalDiscountCents ?? 0;
    const totalRevenue = totalRevenueCents / 100;
    const totalDiscount = totalDiscountCents / 100;

    const byPlan = {};
    if (view === 'byProduct') {
      for (const row of this.chartData) {
        const plan = mode === 'full' ? (row.byPlanFull ?? row.byPlan ?? {}) : (row.byPlan ?? {});
        for (const [key, val] of Object.entries(plan)) {
          byPlan[key] = (byPlan[key] || 0) + val;
        }
      }
    }

    return {
      totalRevenue,
      totalCount: this.total,
      pending: 0,
      failed: 0,
      byPlan: Object.keys(byPlan).length > 0 ? byPlan : undefined,
      totalDiscount,
      amountMode: mode,
      paidCount: s?.paidCount ?? 0,
      promo100Count: s?.promo100Count ?? 0,
      freeCount: s?.freeCount ?? 0
    };
  }

  getProductColor(productKey) {
    let hash = 0;
    const str = String(productKey);
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) - hash) + str.charCodeAt(i);
      hash |= 0;
    }
    const idx = Math.abs(hash) % PRODUCT_COLORS.length;
    return PRODUCT_COLORS[idx];
  }

  getGroupColor(productKey) {
    return this.getProductColor(productKey);
  }

  /**
   * Get purchase type for display: 'paid' | 'promo-100' | 'free'
   */
  getPurchaseType(purchase) {
    const amount = purchase.amount ?? 0;
    const discount = purchase.discountAmount ?? 0;
    if (amount > 0) return 'paid';
    if (discount > 0) return 'promo-100';
    return 'free';
  }

  /**
   * Format amount in cents to currency string
   */
  formatAmount(cents, currency = 'usd') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase()
    }).format((cents || 0) / 100);
  }

  async refreshPurchases() {
    await this.loadPurchases({ ...this.filters, all: true });
  }
}
